import React, { useState, useEffect, useRef } from 'react';
import { MessageCircle, X, Send, Minimize2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import api from '../utils/api';

const ChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sessionId, setSessionId] = useState(null);
  const [userName, setUserName] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [isStarted, setIsStarted] = useState(false);
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (sessionId) {
      const interval = setInterval(fetchMessages, 3000);
      return () => clearInterval(interval);
    }
  }, [sessionId]);

  const fetchMessages = async () => {
    if (!sessionId) return;
    try {
      const response = await api.get(`/chat/messages/${sessionId}`);
      setMessages(response.data);
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  const startChat = async (e) => {
    e.preventDefault();
    if (!userName.trim()) {
      toast.error('Please enter your name');
      return;
    }

    setLoading(true);
    try {
      const response = await api.post(`/chat/start?user_name=${encodeURIComponent(userName)}&user_email=${encodeURIComponent(userEmail)}`);
      setSessionId(response.data.id);
      setIsStarted(true);
      toast.success('Chat started! How can we help you?');
    } catch (error) {
      toast.error('Failed to start chat');
    } finally {
      setLoading(false);
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !sessionId) return;

    const messageData = {
      session_id: sessionId,
      user_id: null,
      user_name: userName,
      user_email: userEmail,
      message: newMessage,
      is_admin: false
    };

    try {
      await api.post('/chat/message', messageData);
      setNewMessage('');
      fetchMessages();
    } catch (error) {
      toast.error('Failed to send message');
    }
  };

  return (
    <>
      {/* Chat Button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-6 right-6 w-16 h-16 bg-secondary rounded-full shadow-lg flex items-center justify-center hover:bg-secondary/90 transition-all z-50 hover:scale-110"
          data-testid="chat-widget-button"
        >
          <MessageCircle className="h-7 w-7 text-white" />
          <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full animate-pulse" />
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div
          className={`fixed bottom-6 right-6 w-96 bg-white rounded-lg shadow-2xl z-50 flex flex-col transition-all ${
            isMinimized ? 'h-16' : 'h-[500px]'
          }`}
          data-testid="chat-window"
        >
          {/* Chat Header */}
          <div className="bg-primary text-white p-4 rounded-t-lg flex items-center justify-between">
            <div className="flex items-center gap-2">
              <MessageCircle className="h-5 w-5" />
              <div>
                <h3 className="font-heading font-bold uppercase">Live Chat</h3>
                <p className="text-xs text-slate-200">We're here to help!</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setIsMinimized(!isMinimized)}
                className="hover:bg-white/20 p-1 rounded"
                data-testid="minimize-chat"
              >
                <Minimize2 className="h-5 w-5" />
              </button>
              <button
                onClick={() => setIsOpen(false)}
                className="hover:bg-white/20 p-1 rounded"
                data-testid="close-chat"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          {!isMinimized && (
            <>
              {/* Chat Start Form */}
              {!isStarted ? (
                <div className="flex-1 p-6 flex flex-col justify-center">
                  <h4 className="font-heading text-xl font-bold text-primary mb-2 uppercase">Start Chat</h4>
                  <p className="text-sm text-slate-600 mb-4">
                    Please provide your details to begin
                  </p>
                  <form onSubmit={startChat} className="space-y-4">
                    <div>
                      <label className="text-sm font-medium mb-1 block">Name *</label>
                      <Input
                        value={userName}
                        onChange={(e) => setUserName(e.target.value)}
                        placeholder="Your name"
                        required
                        data-testid="chat-name-input"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium mb-1 block">Email (Optional)</label>
                      <Input
                        type="email"
                        value={userEmail}
                        onChange={(e) => setUserEmail(e.target.value)}
                        placeholder="your@email.com"
                        data-testid="chat-email-input"
                      />
                    </div>
                    <Button
                      type="submit"
                      className="w-full"
                      disabled={loading}
                      data-testid="start-chat-button"
                    >
                      {loading ? 'Starting...' : 'Start Chat'}
                    </Button>
                  </form>
                </div>
              ) : (
                <>
                  {/* Messages Area */}
                  <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-50" data-testid="chat-messages">
                    {messages.length === 0 ? (
                      <div className="text-center text-slate-500 py-8">
                        <p className="text-sm">No messages yet. Start the conversation!</p>
                      </div>
                    ) : (
                      messages.map((msg) => (
                        <div
                          key={msg.id}
                          className={`flex ${msg.is_admin ? 'justify-start' : 'justify-end'}`}
                        >
                          <div
                            className={`max-w-[75%] rounded-lg p-3 ${
                              msg.is_admin
                                ? 'bg-white border border-slate-200'
                                : 'bg-secondary text-white'
                            }`}
                          >
                            {msg.is_admin && (
                              <p className="text-xs font-medium text-primary mb-1">Support Team</p>
                            )}
                            <p className="text-sm">{msg.message}</p>
                            <p className={`text-xs mt-1 ${msg.is_admin ? 'text-slate-500' : 'text-white/70'}`}>
                              {new Date(msg.created_at).toLocaleTimeString([], {
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </p>
                          </div>
                        </div>
                      ))
                    )}
                    <div ref={messagesEndRef} />
                  </div>

                  {/* Message Input */}
                  <form onSubmit={sendMessage} className="p-4 border-t border-slate-200">
                    <div className="flex gap-2">
                      <Input
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        placeholder="Type your message..."
                        className="flex-1"
                        data-testid="chat-message-input"
                      />
                      <Button type="submit" disabled={!newMessage.trim()} data-testid="send-message-button">
                        <Send className="h-4 w-4" />
                      </Button>
                    </div>
                  </form>
                </>
              )}
            </>
          )}
        </div>
      )}
    </>
  );
};

export default ChatWidget;
